import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, OnDestroy, OnChanges, AfterViewInit, SimpleChanges, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { BunnyPlayerComponent } from '../bunny-player/bunny-player.component';
import { injectFullscreenToggle } from '../../utils/fullscreen-toggle';
import { IconComponent } from '../icon/icon.component';
import { CoursePlayerHeaderComponent } from '../course-player-header/course-player-header.component';
import { WatchProgressChipComponent } from '../watch-progress-chip/watch-progress-chip.component';
import { QuizPanelComponent } from './quiz-panel/quiz-panel.component';
import { VideoQuizSyncService } from '../../../services/video-quiz-sync.service';
import { SecureVideoService } from '../../../services/secure-video.service';
import { VideoProgressService } from '../../../services/video-progress.service';
import { CoursePlayerService, PlayerMode, PlayerConfig } from '../../../services/course-player.service';
import { Subscription, fromEvent } from 'rxjs';
import { QuizQuestion } from './split-screen-player.types';
import { FeedbackFlags } from '../../../instructor/course-builder-dynamic/types/module-creation.types';
import { VideoProgressHandler } from './video-progress-handler';
import { isQuizPassing } from '@shared/utils/quiz-pass.utils';
import {
  getEarnedPoints as _getEarnedPoints,
  getTotalPoints as _getTotalPoints,
  getModeDisplayInfo as _getModeDisplayInfo
} from './split-screen-player.utils';
// KAN-127 Phase 2: use shared formatter + the canonical shouldShowPoints (has total===0 guard)
import { shouldShowPoints as _sharedShouldShowPoints, formatScoreParts, ScoreDisplayParts } from '../../utils/score-display.utils';
import { DebugService } from '../../services/debug.service';
import { ScorePtsPipe } from '../../pipes/score-pts.pipe';

// Re-export for backward compatibility
export type { QuizQuestion } from './split-screen-player.types';

@Component({
  selector: 'app-split-screen-player',
  standalone: true,
  imports: [CommonModule, BunnyPlayerComponent, IconComponent, CoursePlayerHeaderComponent, WatchProgressChipComponent, QuizPanelComponent, ScorePtsPipe],
  templateUrl: './split-screen-player.component.html',
  styleUrls: ['./split-screen-player.component.scss']
})
export class SplitScreenPlayerComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  // KAN-561: the narrowest side-by-side video that is still usable. At the
  // default 50% split the video panel is half the container, so anything below
  // this width means "stack the video over the quiz" instead. Harold's report:
  // a 1280px window with the 350px sidebar open left a ~433px letterboxed video.
  static readonly MIN_SIDE_BY_SIDE_VIDEO_WIDTH_PX = 480;
  // The split ratio the layout resets to when it becomes side-by-side.
  private static readonly DEFAULT_SPLIT_PERCENT = 50;
  // KAN-324: below this PLAYER-CONTAINER width the video + quiz stack vertically
  // instead of sitting side-by-side. Driven off the container (not the window)
  // so an open 350px units sidebar correctly reclaims layout space. Derived,
  // not chosen: 480px video / 50% split = 960px container (KAN-561; was a
  // literal 768, which let a 1280px window + open sidebar render side by side).
  static readonly STACK_BREAKPOINT_PX =
    SplitScreenPlayerComponent.MIN_SIDE_BY_SIDE_VIDEO_WIDTH_PX * 100 / SplitScreenPlayerComponent.DEFAULT_SPLIT_PERCENT;

  // Video/content inputs
  @Input() videoUrl: string = '';
  @Input() videoPoster?: string;
  @Input() videoId?: string;
  // Per-org Bunny: the video's OWN CDN host (videos.cdn_hostname). Forwarded to
  // the embedded bunny-player so per-org videos build the HLS playlist on the
  // org's host instead of the shared host. Empty for legacy/shared videos.
  @Input() cdnHostname: string = '';
  @Input() moduleId?: string;
  @Input() courseId?: string;
  @Input() quizQuestions: QuizQuestion[] = [];
  @Input() quizId?: string;
  @Input() quizCompleted: boolean = false;
  /** Quiz description rendered (linkified) above the questions (KAN-224). */
  @Input() quizDescription: string = '';
  @Input() moduleTitle: string = '';
  @Input() isVideoRequired: boolean = true;
  @Input() requiredWatchPercentage: number = 90;
  @Input() passingScore!: number;

  // Modal-specific inputs
  @Input() isModal: boolean = false;
  @Input() showCloseButton: boolean = false;
  @Input() showNavigation: boolean = false;
  @Input() allowFullscreen: boolean = true;
  @Input() customHeaderContent: string = '';

  // Player mode configuration
  @Input() mode: PlayerMode = 'learner';
  @Input() playerConfig?: PlayerConfig;

  // Enhanced progress tracking
  @Input() unitId?: string;
  @Input() itemType: 'module' | 'unit_quiz' = 'module';

  // Navigation state (for modal use)
  @Input() canGoToPrevious: boolean = false;
  @Input() canGoToNext: boolean = false;
  /** KAN-595: passed straight through to the quiz panel's footer label. */
  @Input() isLastItem: boolean = false;
  /** KAN-597: passed straight through to the quiz panel's footer. */
  @Input() completionPending = false;
  @Input() currentItemIndex: number = 0;
  @Input() totalItems: number = 1;

  // View controls (for learner modal)
  @Input() showTimelineToggle: boolean = false;
  @Input() timelineVisible: boolean = true;

  // Module progress data
  @Input() moduleProgress?: any;
  @Input() useServerScoring = false;
  @Input() quizSubmissionError = false;
  /** KAN-349: structured submit-error info threaded down to the quiz panel. */
  @Input() quizSubmissionErrorInfo: { code?: string; message?: string; details?: any } | null = null;

  // Feedback visibility level for quiz panel (KAN-69) — LEGACY scalar fallback.
  @Input() feedbackVisibility: string = 'indicators_answers';
  // KAN-343: the three authoritative feedback flags, passed straight through to
  // the quiz panel (which gates on them).
  @Input() feedbackFlags?: FeedbackFlags;
  // KAN-328: conditional answer reveal — layered on top of feedbackVisibility.
  @Input() revealAnswersOnlyOnPass = false;

  // KAN-309: configured custom incorrect message, passed through to the quiz
  // panel so instructor preview renders the "Note from instructor" failure banner.
  @Input() customIncorrectMessage: string | null = null;

  // KAN-327: configured custom PASS ("success") message, passed through to the
  // quiz panel so instructor preview renders the success banner (mirror of above).
  @Input() customPassMessage: string | null = null;

  // Outputs
  @Output() onVideoEnd = new EventEmitter<void>();
  @Output() onQuizSubmit = new EventEmitter<{ answers: any[], score: number | null, passed: boolean | null, videoProgress?: { furthestPosition: number, duration: number }, attemptId?: string }>();
  @Output() onComplete = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onNavigateNext = new EventEmitter<void>();
  @Output() onNavigatePrevious = new EventEmitter<void>();
  @Output() onProgressUpdate = new EventEmitter<{ moduleId: string; videoProgress: number; quizScore?: number; completed: boolean }>();
  @Output() onVideoProgressUpdate = new EventEmitter<{ currentTime: number; duration: number; percentage: number; cumulativeTime: number }>();
  @Output() onTimelineToggle = new EventEmitter<void>();

  @ViewChild('divider') divider!: ElementRef;
  @ViewChild('playerContainer') playerContainer!: ElementRef<HTMLElement>;
  // KAN-577: in the stacked (mobile) layout .content-area is itself the
  // scroller (split-screen-player.component.scss `.content-area.stacked`), and
  // this component instance is reused across a module advance, so it retains
  // the previous module's offset exactly as the quiz panel does.
  @ViewChild('contentArea') contentArea?: ElementRef<HTMLElement>;
  @ViewChild(QuizPanelComponent) quizPanel!: QuizPanelComponent;
  // KAN-306 round 5: used to push the freshly-computed watch percentage
  // straight into bunny-player's inline label, synchronously, right after
  // this component computes it -- see onVideoProgress(). Avoids relying on
  // @Input change-detection propagation timing, which is what caused the
  // "label shows 32%, badge shows 33%" transient 1-percentage-point desync.
  @ViewChild(BunnyPlayerComponent) bunnyPlayerRef?: BunnyPlayerComponent;

  // UI state
  isFullscreen = false;
  // KAN-620: element fullscreen on the container (KAN-303) where supported,
  // the video's native iOS player on iPhone; isFullscreen mirrors browser events.
  private readonly fullscreen = injectFullscreenToggle({
    target: () => this.playerContainer?.nativeElement,
    fallbackVideo: () => this.bunnyPlayerRef?.videoPlayer?.nativeElement,
    onChange: isFullscreen => {
      this.isFullscreen = isFullscreen;
      this.cdr.markForCheck();
    }
  });

  /** Hides the header button where no fullscreen is possible (quiz-only module on iPhone). */
  get fullscreenAvailable(): boolean {
    return this.fullscreen.isAvailable;
  }
  isMobile = false;
  splitPosition = 50;
  isDragging = false;

  /** Whether this item has video content (false for standalone quizzes). */
  get hasVideo(): boolean {
    return !!(this.videoUrl || this.videoId);
  }

  /** Whether this module has a graded quiz (questions or a quiz id present). */
  get hasQuiz(): boolean {
    return this.quizQuestions.length > 0 || !!this.quizId;
  }

  // Player configuration state
  currentPlayerConfig!: PlayerConfig;
  effectiveVideoRequirement: boolean = true;

  // Quiz state (proxy properties synced from QuizPanelComponent for status bar)
  quizSubmitted = false;
  quizPassed = false;
  autoGradedScore = 0;
  autoGradedMaxScore = 0;
  isPartiallyGraded = false;

  // Quiz sync state
  currentQuestionIndex = 0;

  // Video state (synced from handler for template bindings)
  currentVideoTime = 0;
  videoDuration = 0;
  isVideoPlaying = false;
  videoWatchPercentage = 0;
  videoCompleted = false;
  videoCompletedFromBackend = false;
  cumulativeWatchTime = 0;
  lastTrackedTime = 0;
  initialLastTrackedTime = 0;
  videoEnded = false;
  progressLoading = true;
  secureVideoUrl: string = '';
  isLoadingVideo = false;

  /**
   * The ONE watch-progress number this component publishes (KAN-306), read by
   * every surface it owns: the status-bar chip, the in-player chip, and the
   * quiz panel. Defined once here so no surface can drift into its own
   * expression -- that drift is the whole bug (player "97% watched" vs badge
   * "Video Complete - 94%").
   *
   * This is the max-watched high-water mark, never the playhead. The
   * moduleProgress fallback preserves the old badges' behaviour: on remount
   * this component's own field is 0 until the first tick lands, and the
   * persisted value is what should show meanwhile.
   */
  get displayedWatchPercentage(): number {
    return this.videoWatchPercentage || this.moduleProgress?.videoWatchPercentage || 0;
  }

  /** Companion to displayedWatchPercentage -- same fallback, same reason. */
  get displayedWatchCompleted(): boolean {
    return this.videoCompleted || !!this.moduleProgress?.videoCompleted;
  }

  private destroyRef = inject(DestroyRef);

  // KAN-324: observes the player container so the stack/side-by-side decision
  // tracks the ACTUAL available width (sidebar toggles, modal resize, window
  // resize) rather than only window.innerWidth.
  private resizeObserver: ResizeObserver | null = null;

  // Drag cleanup references
  private activeDragMoveHandler: ((e: MouseEvent | TouchEvent) => void) | null = null;
  private activeDragEndHandler: (() => void) | null = null;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Video progress handler (manages all video progress logic)
  private videoHandler!: VideoProgressHandler;

  constructor(
    private syncService: VideoQuizSyncService,
    private secureVideoService: SecureVideoService,
    private videoProgressService: VideoProgressService,
    private coursePlayerService: CoursePlayerService,
    private cdr: ChangeDetectorRef,
    public debug: DebugService
  ) {
    this.currentPlayerConfig = this.coursePlayerService.getPlayerConfig('learner');
    this.videoHandler = this.createVideoHandler();
  }

  private createVideoHandler(): VideoProgressHandler {
    return new VideoProgressHandler({
      videoProgressService: this.videoProgressService,
      secureVideoService: this.secureVideoService,
      subscriptions: this.subscriptions,
      getModuleId: () => this.moduleId,
      getCourseId: () => this.courseId,
      getVideoId: () => this.videoId,
      getVideoUrl: () => this.videoUrl,
      getMode: () => this.mode,
      getItemType: () => this.itemType,
      getRequiredWatchPercentage: () => this.requiredWatchPercentage,
      getIsVideoRequired: () => this.isVideoRequired,
      getHasQuiz: () => this.hasQuiz
    });
  }

  /** Sync component properties from the video handler state for template bindings. */
  private syncFromHandler(): void {
    const s = this.videoHandler.getState();
    this.currentVideoTime = s.currentVideoTime;
    this.videoDuration = s.videoDuration;
    this.isVideoPlaying = s.isVideoPlaying;
    this.videoWatchPercentage = s.videoWatchPercentage;
    this.videoCompleted = s.videoCompleted;
    this.videoCompletedFromBackend = s.videoCompletedFromBackend;
    this.cumulativeWatchTime = s.cumulativeWatchTime;
    this.lastTrackedTime = s.lastTrackedTime;
    this.initialLastTrackedTime = s.initialLastTrackedTime;
    this.videoEnded = s.videoEnded;
    this.progressLoading = s.progressLoading;
    this.secureVideoUrl = s.secureVideoUrl;
    this.isLoadingVideo = s.isLoadingVideo;
  }

  ngOnChanges(changes: SimpleChanges) {
    // KAN-597 round 3: the learner modal keeps this component alive across
    // module changes and only rebinds inputs, so the video gate must follow
    // them. It used to be derived once in ngOnInit: a quiz-only module reached
    // from a video module stayed gated on the old video (Submit Quiz disabled
    // with no reason shown), and the reverse move left a video module's quiz
    // un-gated.
    const gateInputChanged = ['isVideoRequired', 'playerConfig']
      .some(key => changes[key] && !changes[key].firstChange);
    if (gateInputChanged) {
      this.applyPlayerConfiguration();
    }

    const videoUrlChanged = changes['videoUrl'] &&
      changes['videoUrl'].currentValue !== changes['videoUrl'].previousValue &&
      !changes['videoUrl'].firstChange;

    const videoIdChanged = changes['videoId'] &&
      changes['videoId'].currentValue !== changes['videoId'].previousValue &&
      !changes['videoId'].firstChange;

    const moduleIdChanged = changes['moduleId'] &&
      changes['moduleId'].currentValue !== changes['moduleId'].previousValue &&
      !changes['moduleId'].firstChange;

    if (videoUrlChanged || videoIdChanged || moduleIdChanged) {
      // Reset quiz state when switching modules so stale scores don't persist
      if (moduleIdChanged) {
        // KAN-577 A (stacked/mobile surface): start the new module at the top.
        const contentEl = this.contentArea?.nativeElement;
        if (contentEl) {
          contentEl.scrollTop = 0;
        }
        this.quizSubmitted = false;
        this.quizPassed = false;
        this.autoGradedScore = 0;
        this.autoGradedMaxScore = 0;
        this.isPartiallyGraded = false;
      }

      // KAN-203: Clear per-module video DISPLAY state synchronously so the
      // template never binds stale "Video Complete / 100% watched" values
      // from the previous module to the bunny-player or quiz panel while
      // the async resetVideoState() is still flushing in-flight saves. This
      // matters most when two modules share the same video asset — without
      // this sync reset, the user sees a brief but real "Submit Quiz"-enabled
      // window on Module 2 that lets them hit the backend's
      // VIDEO_COMPLETION_REQUIRED 400.
      this.videoHandler.resetStateSync();
      this.syncFromHandler();

      this.videoHandler.resetVideoState().then(() => {
        // Sync the reset state so the template sees secureVideoUrl = '',
        // which destroys the bunny-player before the new video loads
        this.syncFromHandler();
        this.cdr.detectChanges();

        this.videoHandler.loadNewVideo().then(() => {
          this.syncFromHandler();
          this.cdr.detectChanges();
          this.syncService.updateQuizState({
            totalQuestions: this.quizQuestions.length,
            isLocked: this.isVideoRequired && !this.videoCompleted
          });
        });
      });
    }

    // Update status bar when server scores arrive via moduleProgress
    if (changes['moduleProgress'] && !changes['moduleProgress'].firstChange) {
      const progress = changes['moduleProgress'].currentValue;
      if (progress && progress.quizScore !== null && progress.quizScore !== undefined) {
        this.quizSubmitted = true;
        // KAN-239 (reopened): the FRESH-SUBMIT badge must use the SAME raw
        // boundary as the saved-progress badge. progress.quizScore is the backend
        // INTEGER-rounded percentage (80 for 79.5%), so comparing it to the
        // threshold rounded a sub-threshold attempt up into a PASS while the
        // result footer correctly failed it. Route through isModuleProgressPassed,
        // which prefers raw earned/total points over the rounded score.
        this.quizPassed = this.isModuleProgressPassed(progress);
        if (progress.quizEarnedPoints !== null && progress.quizEarnedPoints !== undefined) {
          this.autoGradedScore = progress.quizEarnedPoints;
          this.autoGradedMaxScore = progress.quizTotalPoints;
        }
        this.cdr.detectChanges();
      }
    }
  }

  ngOnInit() {
    // Validate passingScore input
    if (this.passingScore === null || this.passingScore === undefined) {
      this.passingScore = 80;
    } else if (typeof this.passingScore !== 'number' || this.passingScore < 0 || this.passingScore > 100) {
      this.passingScore = 80;
    }

    // Apply player configuration based on mode
    this.applyPlayerConfiguration();

    this.checkScreenSize();
    fromEvent(window, 'resize').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.checkScreenSize());

    // Set up synchronization
    this.setupSynchronization();

    // Load secure video URL if video ID is provided
    if (this.videoId) {
      this.videoHandler.loadSecureVideoUrl();
    } else {
      this.videoHandler.setState({ secureVideoUrl: this.videoUrl });
    }
    this.syncFromHandler();

    // Load existing video progress and initialize
    this.initializeAsync();
  }

  private async initializeAsync(): Promise<void> {
    await this.videoHandler.loadExistingProgress();
    this.syncFromHandler();
  }

  ngAfterViewInit(): void {
    // KAN-324: prefer measuring the real container width via ResizeObserver.
    // The window 'resize' listener in ngOnInit remains as a fallback for
    // environments without ResizeObserver, but the observer also fires on
    // sidebar collapse/expand and modal resize — cases window resize misses.
    const el = this.playerContainer?.nativeElement;
    if (!el) return;

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? el.clientWidth;
        this.applyResponsiveLayout(width);
        // Zoneless: the RO callback runs outside Angular, so re-render explicitly.
        this.cdr.detectChanges();
      });
      this.resizeObserver.observe(el);
    }

    // Prime the layout from the initial measured width so the first paint is
    // correct even before the observer's first callback.
    this.applyResponsiveLayout(el.clientWidth || window.innerWidth);
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    // Fire-and-forget: Angular doesn't await async ngOnDestroy
    if (this.moduleId) {
      this.videoProgressService.stopTracking();
    }

    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Clean up any active drag handlers
    if (this.activeDragMoveHandler) {
      document.removeEventListener('mousemove', this.activeDragMoveHandler as EventListener);
      document.removeEventListener('touchmove', this.activeDragMoveHandler as EventListener);
      this.activeDragMoveHandler = null;
    }
    if (this.activeDragEndHandler) {
      document.removeEventListener('mouseup', this.activeDragEndHandler as EventListener);
      document.removeEventListener('touchend', this.activeDragEndHandler as EventListener);
      this.activeDragEndHandler = null;
    }

    // Resize listener cleanup is handled automatically via takeUntilDestroyed
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.syncService.reset();
  }

  private applyPlayerConfiguration(): void {
    this.currentPlayerConfig = this.playerConfig ||
      this.coursePlayerService.getPlayerConfig(this.mode);
    this.effectiveVideoRequirement = this.currentPlayerConfig.enforceVideoCompletion &&
      this.isVideoRequired;

    // Wave 1c: Re-sync the root VideoProgressService singleton with THIS
    // component's mode. VideoProgressService is provided in 'root' and caches
    // `currentPlayerMode`; a previous instructor preview that torn down without
    // resetting the mode would otherwise cause this learner mount to silently
    // skip backend saves. Calling this on every mount fixes the regression.
    this.videoProgressService.updatePlayerMode(this.mode);
  }

  getModeDisplayInfo(): { name: string; description: string; color: string } {
    return _getModeDisplayInfo(this.mode);
  }

  closePlayer(): void {
    this.onClose.emit();
  }

  navigatePrevious(): void {
    if (this.canGoToPrevious) {
      this.onNavigatePrevious.emit();
    }
  }

  navigateNext(): void {
    if (this.canGoToNext) {
      this.onNavigateNext.emit();
    }
  }

  private setupSynchronization() {
    const quizSub = this.syncService.quizState$.subscribe(state => {
      if (state.currentQuestionIndex !== this.currentQuestionIndex) {
        this.scrollToQuestion(state.currentQuestionIndex);
      }
    });
    this.subscriptions.push(quizSub);

    const timestampedQuestions = this.quizQuestions
      .map((q, index) => ({ time: q.timeMarker || 0, questionIndex: index, action: 'highlight' as const }))
      .filter(ts => ts.time > 0);

    if (timestampedQuestions.length > 0) {
      this.syncService.setSyncedTimestamps(timestampedQuestions);
    }
  }

  checkScreenSize() {
    // KAN-324: measure the actual player container when available so an open
    // units sidebar (which shrinks the container without changing the window)
    // is accounted for. Falls back to window width before the view is ready.
    const width = this.playerContainer?.nativeElement?.clientWidth || window.innerWidth;
    this.applyResponsiveLayout(width);
  }

  /**
   * KAN-324: decide stack (vertical) vs side-by-side from the given width and
   * reset the split ratio on transitions. `width === 0` (not yet laid out)
   * defaults to side-by-side rather than collapsing to stacked.
   */
  applyResponsiveLayout(width: number): void {
    const wasStacked = this.isMobile;
    this.isMobile = width > 0 && width < SplitScreenPlayerComponent.STACK_BREAKPOINT_PX;
    if (this.isMobile) {
      this.splitPosition = 100;
    } else if (wasStacked && !this.isMobile) {
      this.splitPosition = 50;
    }
    this.cdr.markForCheck();
  }

  onVideoEnded() {
    this.videoEnded = true;
    this.videoHandler.setState({ videoEnded: true });
    this.onVideoEnd.emit();
    this.syncService.updateVideoState({ hasEnded: true });
  }

  onVideoProgress(timeData: { currentTime: number; duration: number }) {
    // KAN-306 round 4 addendum: sync component state to handler (tests may
    // set properties directly) -- but NEVER as a blind overwrite for the
    // handler's own authoritative, monotonic fields. loadExistingProgress()
    // hydrates the HANDLER's internal state asynchronously; if even one
    // native timeupdate tick fires before that hydration's
    // syncFromHandler() call has copied the fresh value into these
    // COMPONENT mirror fields, a blind overwrite here would clobber the
    // handler's already-correct 100%/completed state back down to the
    // component's still-default 0/false -- poisoning the very "previous"
    // value handleVideoProgress()'s monotonic floor depends on (this was
    // observed live: the status-bar badge read "Video Complete - 28%"
    // while the player-controls label, which reads bunny-player's own
    // maxWatchedPosition @Input fresh each time with no "previous" to
    // poison, correctly showed 100%). Math.max/OR-merge against whatever
    // the handler already holds so a stale component mirror can only ever
    // ADD information, never take it away -- this preserves the existing
    // test pattern (a fresh handler starts at 0, so the component's
    // injected value always wins) while protecting a genuinely-hydrated
    // handler from regression.
    const currentHandlerState = this.videoHandler.getState();
    this.videoHandler.setState({
      videoDuration: this.videoDuration,
      cumulativeWatchTime: Math.max(currentHandlerState.cumulativeWatchTime, this.cumulativeWatchTime),
      lastTrackedTime: this.lastTrackedTime,
      initialLastTrackedTime: this.initialLastTrackedTime,
      videoWatchPercentage: Math.max(currentHandlerState.videoWatchPercentage, this.videoWatchPercentage),
      videoCompleted: currentHandlerState.videoCompleted || this.videoCompleted,
      videoCompletedFromBackend: currentHandlerState.videoCompletedFromBackend || this.videoCompletedFromBackend
    });

    const result = this.videoHandler.handleVideoProgress(timeData);

    // Sync state from handler
    this.currentVideoTime = result.currentVideoTime;
    this.videoDuration = result.videoDuration;
    this.cumulativeWatchTime = result.cumulativeWatchTime;
    this.lastTrackedTime = result.lastTrackedTime;
    this.videoWatchPercentage = result.videoWatchPercentage;
    this.videoCompleted = result.videoCompleted;
    // videoCompletedFromBackend isn't part of VideoProgressResult -- read it
    // straight from the handler's internal state so the component's own
    // mirror (used by quiz-panel's videoMeetsRequirement gate) never lags
    // behind what the setState() merge above just protected.
    this.videoCompletedFromBackend = this.videoHandler.getState().videoCompletedFromBackend;

    // KAN-306 round 5: push this tick's freshly-computed percentage
    // straight into bunny-player's inline label SYNCHRONOUSLY, rather than
    // waiting for the [maxWatchedPosition] @Input binding to propagate on
    // the next change-detection pass. bunny-player's own native
    // (timeupdate) listener computes its label from whatever
    // maxWatchedPosition currently holds BEFORE this component's handler
    // has finished updating cumulativeWatchTime for the same tick, so
    // without this the label can render one tick stale relative to the
    // badge above -- the source of the observed 1-percentage-point
    // transient desync. Both surfaces now render the identical integer.
    this.bunnyPlayerRef?.syncWatchedPercentage(this.videoWatchPercentage);

    if (result.rewound) {
      return;
    }

    // Emit video progress update for modal integration
    if (result.progressEvent) {
      this.onVideoProgressUpdate.emit(result.progressEvent);
    }

    // Update sync service
    this.syncService.updateVideoState({
      currentTime: timeData.currentTime,
      duration: timeData.duration
    });

    // Check if any questions should be highlighted based on time markers
    if (this.quizQuestions.some(q => q.timeMarker)) {
      this.highlightRelevantQuestion();
    }

    // KAN-306/KAN-307: zoneless -- this handler mutates plain fields
    // (videoWatchPercentage, cumulativeWatchTime, etc.) from a native <video>
    // timeupdate callback. Without this, the write persists but the
    // status-bar badge, quiz-panel progress text, and bunny-player's
    // maxWatchedPosition binding stay visually frozen until an unrelated
    // re-render happens to sweep through.
    this.cdr.markForCheck();
  }

  highlightRelevantQuestion() {
    const relevantIndex = this.quizQuestions.findIndex(q =>
      q.timeMarker && Math.abs(q.timeMarker - this.currentVideoTime) < 5
    );
    if (relevantIndex !== -1) {
      this.currentQuestionIndex = relevantIndex;
    }
  }

  startDrag(event: MouseEvent | TouchEvent) {
    if (this.isMobile) return;
    event.preventDefault();
    event.stopPropagation();

    const startX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    let hasMoved = false;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const currentX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
      if (!hasMoved && Math.abs(currentX - startX) < 3) return;

      hasMoved = true;
      this.isDragging = true;

      const container = this.divider.nativeElement.parentElement as HTMLElement;
      const containerRect = container.getBoundingClientRect();
      const relativeX = currentX - containerRect.left;
      const newPosition = (relativeX / containerRect.width) * 100;
      // KAN-324: allow dragging the video up to 75% (was 62%) so the learner
      // can reclaim room on constrained side-by-side widths.
      this.splitPosition = Math.max(50, Math.min(75, newPosition));
      this.cdr.markForCheck();
    };

    const handleEnd = () => {
      this.isDragging = false;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchend', handleEnd);
      this.cdr.markForCheck();
      this.activeDragMoveHandler = null;
      this.activeDragEndHandler = null;
    };

    // Store references for cleanup in ngOnDestroy
    this.activeDragMoveHandler = handleMove;
    this.activeDragEndHandler = handleEnd;

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchend', handleEnd);
  }

  toggleFullscreen() {
    // KAN-303: request fullscreen on the PLAYER CONTAINER itself, not
    // document.documentElement. The content-hub preview renders this player
    // deep inside a DaisyUI .modal whose transform/fixed positioning creates a
    // containing block; fullscreening <html> left the container's
    // position:fixed trapped inside the modal-box, collapsing the player into a
    // narrow horizontal strip. Promoting the container element to the
    // browser's top layer escapes that containing block, and the :fullscreen /
    // :-webkit-full-screen CSS rules force it to fill the viewport.
    // The toggle's fullscreenchange listener keeps isFullscreen in sync when
    // the user leaves via Esc; on iPhone it opens the native video player.
    this.fullscreen.toggle();
  }

  /** Reconcile isFullscreen with the browser's actual fullscreen element. */
  private syncFullscreenState(): void {
    this.fullscreen.sync();
  }

  onQuizSubmitted(event: { answers: any[], score: number | null, passed: boolean | null, videoProgress?: { furthestPosition: number, duration: number }, attemptId?: string, autoGradedScore?: number, autoGradedMaxScore?: number, isPartiallyGraded?: boolean }) {
    // In server-scoring mode, score/passed are null — don't update status bar yet
    if (event.score !== null && event.score !== undefined) {
      this.quizSubmitted = true;
      this.quizPassed = event.passed ?? false;
      this.autoGradedScore = event.autoGradedScore ?? event.score;
      this.autoGradedMaxScore = event.autoGradedMaxScore ?? 100;
      this.isPartiallyGraded = event.isPartiallyGraded ?? false;
    }
    this.cdr.detectChanges();

    // KAN-348: forward attemptId (grading runs against the frozen snapshot) and
    // the KAN-325 videoProgress snapshot — this re-emit previously rebuilt the
    // payload with only 3 fields, silently dropping extra fields at this hop.
    this.onQuizSubmit.emit({
      answers: event.answers,
      score: event.score,
      passed: event.passed,
      videoProgress: event.videoProgress,
      attemptId: event.attemptId
    });
  }

  onQuizRetried() {
    this.quizSubmitted = false;
    this.quizPassed = false;
    this.autoGradedScore = 0;
    this.autoGradedMaxScore = 0;
    this.isPartiallyGraded = false;
    this.cdr.detectChanges();
  }

  onSubmissionRestored(event: { quizSubmitted: boolean; quizPassed: boolean; autoGradedScore: number; autoGradedMaxScore: number; isPartiallyGraded: boolean }) {
    this.quizSubmitted = event.quizSubmitted;
    this.quizPassed = event.quizPassed;
    this.autoGradedScore = event.autoGradedScore;
    this.autoGradedMaxScore = event.autoGradedMaxScore;
    this.isPartiallyGraded = event.isPartiallyGraded;
    this.cdr.detectChanges();
  }

  completeModule() {
    this.onComplete.emit();
  }

  scrollToQuestion(index: number) {
    this.currentQuestionIndex = index;
    const element = document.getElementById(`question-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  async onVideoReady(videoElement: HTMLVideoElement | HTMLIFrameElement): Promise<void> {
    await this.videoHandler.onVideoReady(videoElement);
    this.syncFromHandler();
  }

  onBunnyVideoLoaded(event: { duration: number }): void {
    this.videoHandler.handleBunnyVideoLoaded(event);
    this.syncFromHandler();
  }

  // Kept for backward compatibility but no longer called internally
  loadExistingProgress(): Promise<void> {
    return this.videoHandler.loadExistingProgress().then(() => {
      this.syncFromHandler();
    });
  }

  /**
   * KAN-127 Phase 2: shouldShowPoints now delegates to the SHARED util from
   * score-display.utils.ts, which includes the `total === 0` guard that the
   * local copy in split-screen-player.utils.ts was missing.
   * The local copy is kept in split-screen-player.utils.ts for backward
   * compat of any external callers; only the SSP component switches here.
   */
  shouldShowPoints(data: any): boolean {
    return _sharedShouldShowPoints(data);
  }

  getEarnedPoints(data: any): number | null {
    return _getEarnedPoints(data);
  }

  getTotalPoints(data: any): number | null {
    return _getTotalPoints(data);
  }

  /**
   * KAN-127 Phase 2: score display parts for the "just-submitted" status bar badge.
   * Data shape: component's own autoGradedScore/autoGradedMaxScore/quizPassed fields.
   */
  get justSubmittedScoreParts(): ScoreDisplayParts | null {
    if (this.autoGradedMaxScore <= 0) {
      return null;
    }
    const data = {
      quizEarnedPoints: this.autoGradedScore,
      quizTotalPoints: this.autoGradedMaxScore,
      quizScore: null,
      passed: this.quizPassed,
    };
    return formatScoreParts(data, this.passingScore);
  }

  /**
   * KAN-127 Phase 2: score display parts for the "saved-progress" status bar badge.
   * Consumes moduleProgress (camelCase or snake_case) via formatScoreParts.
   */
  savedProgressScoreParts(progress: any): ScoreDisplayParts | null {
    if (!progress) {
      return null;
    }
    return formatScoreParts(progress, this.passingScore);
  }

  /**
   * KAN-239 (reopened): Decide pass/fail for the saved-progress badge so it
   * NEVER diverges from the result footer. Raw earned/total points are ground
   * truth — they reproduce the exact `computeQuizResult` boundary the footer
   * uses — so when they are present we recompute pass/fail from them and IGNORE
   * any stored `quizPassed` boolean. This corrects already-stored bad data
   * (e.g. a backend that aliased quizPassed to "completed", marking a
   * sub-threshold 795/1000 @ 80% attempt as passed) without a migration, and
   * keeps the badge identical to the footer's raw-boundary verdict.
   *
   * Precedence:
   *   1. Raw points (earned/total) when available -> isQuizPassing on the exact
   *      percentage. This is the authoritative, footer-matching path.
   *   2. Stored `quizPassed` boolean — only when raw points are absent. This is
   *      the instructor-preview path, whose emit carries no points but does
   *      carry the correct boolean (isQuizPassing on the raw 1dp percentage).
   *   3. Last-resort: the integer-rounded `quizScore` via isQuizPassing.
   *
   * Display rounding must never alter the conditional `percentage >= threshold`
   * (isQuizPassing rounds HALF_UP to 1dp on both sides, mirroring the backend).
   */
  isModuleProgressPassed(data: any): boolean {
    if (!data) return false;
    const earned = _getEarnedPoints(data);
    const total = _getTotalPoints(data);
    if (earned !== null && earned !== undefined && total !== null && total > 0) {
      return isQuizPassing((earned / total) * 100, this.passingScore);
    }
    if (typeof data.quizPassed === 'boolean') {
      return data.quizPassed;
    }
    return isQuizPassing(data.quizScore ?? 0, this.passingScore);
  }
}
